"use client";

import React, {
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  forwardRef,
} from "react";
import PropTypes from "prop-types";

const AltchaGate = forwardRef(function AltchaGate(
  {
    className = "mb-6 flex justify-center",
    onToken,
    challengeUrl = "/api/altcha/challenge",
  },
  ref,
) {
  const widgetRef = useRef(null);
  const [widgetKey, setWidgetKey] = useState(0);
  const [isReady, setIsReady] = useState(false);

  const isProd = process.env.NODE_ENV === "production";

  useEffect(() => {
    let cancelled = false;

    async function boot() {
      if (!isProd) {
        onToken?.("dev-bypass");
        setIsReady(true);
        return;
      }

      try {
        await import("altcha");
        await import("altcha/i18n/all");

        if (typeof window !== "undefined" && window.customElements) {
          await window.customElements.whenDefined("altcha-widget");
        }

        if (!cancelled) {
          setIsReady(true);
        }
      } catch (error) {
        console.error("ALTCHA boot error:", error);
        if (!cancelled) {
          setIsReady(false);
          onToken?.(null);
        }
      }
    }

    boot();

    return () => {
      cancelled = true;
    };
  }, [isProd, onToken]);

  useEffect(() => {
    if (!isProd || !isReady) return;

    const widget = widgetRef.current;
    if (!widget) return;

    const handleStateChange = (ev) => {
      const state = ev?.detail?.state;
      const payload = ev?.detail?.payload || null;

      if (state === "verified" && payload) {
        onToken?.(payload);
        return;
      }

      if (
        state === "unverified" ||
        state === "error" ||
        state === "expired"
      ) {
        onToken?.(null);
      }
    };

    const attach = () => {
      widget.addEventListener("statechange", handleStateChange);
    };

    const raf = window.requestAnimationFrame(attach);

    return () => {
      window.cancelAnimationFrame(raf);
      widget.removeEventListener("statechange", handleStateChange);
    };
  }, [isProd, isReady, onToken, widgetKey]);

  useImperativeHandle(ref, () => ({
    reset() {
      if (!isProd) {
        onToken?.("dev-bypass");
        return;
      }

      onToken?.(null);

      const widget = widgetRef.current;
      if (widget?.reset) {
        widget.reset();
      } else {
        setWidgetKey((current) => current + 1);
      }
    },
  }));

  if (!isProd) {
    return <input type="hidden" name="altcha" value="dev-bypass" />;
  }

  return (
    <div className={className}>
      {!isReady ? (
        <div className="w-full min-h-[84px]" />
      ) : (
        <altcha-widget
          key={widgetKey}
          ref={widgetRef}
          challengeurl={challengeUrl}
          language="es"
          hidefooter
          debug
        ></altcha-widget>
      )}
    </div>
  );
});

AltchaGate.propTypes = {
  className: PropTypes.string,
  onToken: PropTypes.func,
  challengeUrl: PropTypes.string,
};

export default AltchaGate;